<script setup lang="ts">
import type { NavigationTab } from '~/components/ContextualNavigationHeader';

import { cn } from '@/utils';

import { LoadingBadge } from '~/components/LoadingBadge';

const props = defineProps<{
	tabs: NavigationTab[];
	class?: string;
}>();

const emit = defineEmits<{
	tabClick: [tab: NavigationTab];
}>();

const route = useRoute();
const router = useRouter();

// SSR-friendly: false no servidor e no client inicial, true após mount
// Isso garante que SSR HTML === Client hydration HTML (sem mismatch)
const isMounted = ref(false);
onMounted(() => {
	isMounted.value = true;
});

const activeTabId = computed(() => {
	const currentPath = route.path;

	const activeTab = props.tabs.find(tab => {
		if (tab.route.includes('[') && tab.route.includes(']')) {
			const basePath = tab.route.split('/[')[0] || '';
			return currentPath.startsWith(basePath);
		}

		return currentPath === tab.route;
	});

	return activeTab?.id || null;
});

const tabsWithActiveState = computed(() => {
	return props.tabs.map(tab => ({
		...tab,
		isActive: isActiveTab(tab.id)
	}));
});

const isActiveTab = (tabId: string): boolean => {
	return tabId === activeTabId.value;
};

function handleTabClick(tab: NavigationTab): void {
	if (isActiveTab(tab.id)) return;

	emit('tabClick', tab);
	router.push(tab.route);
}
</script>

<template>
	<nav
		:class="
			cn(
				'flex size-full items-center overflow-hidden bg-primary transition-all duration-300 ease-in md:max-h-none',
				props.class
			)
		"
	>
		<div
			class="no-scrollbar flex w-full items-center justify-start gap-5 overflow-auto px-5 pt-3 md:pt-0 lg:gap-6 lg:pl-12"
		>
			<div
				v-for="tab in tabsWithActiveState"
				:key="`contextual-nav-tab-${tab.id}`"
				class="flex items-center"
			>
				<Button
					:variant="tab.isActive ? 'primary' : 'secondary'"
					class="flex w-full select-none justify-center gap-2 self-center rounded-b-none border-0 border-b-[3px] border-transparent! bg-transparent! px-0! py-0 pb-3 text-base text-white ring-0! md:py-4"
					:class="[
						tab.isActive
							? 'border-beige!'
							: 'border-white-dark-4 text-white hover:border-primary'
					]"
					@click="handleTabClick(tab)"
				>
					<span
						class="font-normal"
						:class="{ 'text-white-dark-4': !tab.isActive }"
					>
						{{ tab.label }}
					</span>
					<!-- Contador com loading para tabs que carregam count no client -->
					<template v-if="tab.showCountLoading">
						<!-- SSR e client inicial: loading (garante hydration match) -->
						<LoadingBadge v-if="!isMounted" />
						<!-- Client após mount: mostra count -->
						<template v-else>
							<span
								v-if="tab.count && tab.count > 0"
								class="rounded-full bg-primary-light-3 px-[6px] text-center font-Inter text-xs font-medium leading-[150%] text-white"
							>
								{{ tab.count }}
							</span>
							<span v-else class="invisible px-[6px] text-xs">0</span>
						</template>
					</template>
					<!-- Contador estático para tabs sem loading -->
					<template v-else>
						<span
							v-if="tab.count && tab.count > 0"
							class="rounded-full bg-primary-light-3 px-[6px] text-center font-Inter text-xs font-medium leading-[150%] text-white"
						>
							{{ tab.count }}
						</span>
					</template>
				</Button>
			</div>
		</div>
	</nav>
</template>
